import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MapPin, Calendar, CreditCard, 
  Shield, CheckCircle, AlertCircle, Camera, Edit,
  Users, Share2, Copy
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Navbar from './Navbar';

const Profile = () => {
  const { user, sendOTP, verifyOTP, updateProfile, updateKYC, refreshUser } = useUser();
  const [activeTab, setActiveTab] = useState('personal');
  const [editing, setEditing] = useState(false);
  const [otpStep, setOtpStep] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [profileForm, setProfileForm] = useState({
    full_name: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [kycForm, setKycForm] = useState({
    aadhar_number: '',
    pan_number: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        full_name: user.full_name || '',
        date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        pincode: user.pincode || ''
      });
      
      setKycForm({
        aadhar_number: user.aadhar_number || '',
        pan_number: user.pan_number || ''
      });
    }
  }, [user]);

  const handleSendOTP = async (type) => {
    setLoading(true);
    setError('');
    
    const result = await sendOTP(
      type === 'phone' ? user.phone : null,
      type === 'email' ? user.email : null
    );
    
    if (result.success) {
      setOtpStep(type);
      setSuccess(`OTP sent to your ${type}`);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError('');
    
    const result = await verifyOTP(
      otpStep === 'phone' ? user.phone : null,
      otpStep === 'email' ? user.email : null,
      otp
    );
    
    if (result.success) {
      setSuccess(`${otpStep} verified successfully!`);
      setOtpStep('');
      setOtp('');
      await refreshUser();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setError('');
    
    const result = await updateProfile(profileForm);
    
    if (result.success) {
      setSuccess('Profile updated successfully!');
      setEditing(false);
      await refreshUser();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleKYCUpdate = async () => {
    setLoading(true);
    setError('');
    
    const result = await updateKYC(kycForm);
    
    if (result.success) {
      setSuccess('KYC details updated successfully!');
      await refreshUser();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const getVerificationBadge = () => {
    switch (user?.verification_level) {
      case 'BASIC':
        return <Badge className="bg-gray-100 text-gray-800">Basic</Badge>;
      case 'PHONE_VERIFIED':
        return <Badge className="bg-blue-100 text-blue-800">Phone Verified</Badge>;
      case 'EMAIL_VERIFIED':
        return <Badge className="bg-green-100 text-green-800">Email Verified</Badge>;
      case 'KYC_VERIFIED':
        return <Badge className="bg-red-100 text-red-800">KYC Verified</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Basic</Badge>;
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="h-12 w-12 text-red-600" />
                </div>
                <button className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-md border border-gray-200">
                  <Camera className="h-4 w-4 text-gray-600" />
                </button>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{user.full_name}</h1>
                  {getVerificationBadge()}
                </div>
                <p className="text-gray-600 mb-1">@{user.username}</p>
                <p className="text-sm text-gray-500">Member since {new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-200 text-red-600 rounded-lg">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-200 text-green-600 rounded-lg">
            {success}
          </div>
        )}

        {/* OTP Verification Modal */}
        {otpStep && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">
                Verify {otpStep === 'phone' ? 'Phone' : 'Email'}
              </h3>
              <p className="text-gray-600 mb-4">
                Enter the 6-digit OTP sent to your {otpStep}
              </p>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                placeholder="Enter OTP"
                maxLength={6}
              />
              <div className="flex space-x-3">
                <Button onClick={handleVerifyOTP} disabled={loading || otp.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
                <Button variant="outline" onClick={() => setOtpStep('')}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-0">
                <nav className="space-y-1">
                  {[
                    { id: 'personal', label: 'Personal Info', icon: User },
                    { id: 'verification', label: 'Verification', icon: Shield },
                    { id: 'referral', label: 'Referral', icon: Users },
                    { id: 'security', label: 'Security', icon: CheckCircle }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        activeTab === tab.id ? 'bg-red-50 text-red-600 border-r-2 border-red-600' : 'text-gray-600'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {activeTab === 'personal' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Personal Information</CardTitle>
                    <Button
                      variant="outline"
                      onClick={() => setEditing(!editing)}
                      className="flex items-center space-x-2"
                    >
                      <Edit className="h-4 w-4" />
                      <span>{editing ? 'Cancel' : 'Edit'}</span>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      {editing ? (
                        <input
                          type="text"
                          value={profileForm.full_name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-gray-900">{user.full_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                      {editing ? (
                        <input
                          type="date"
                          value={profileForm.date_of_birth}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : 'Not set'}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                      {editing ? (
                        <textarea
                          value={profileForm.address}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, address: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          rows={3}
                        />
                      ) : (
                        <p className="text-gray-900">{user.address || 'Not set'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      {editing ? (
                        <input
                          type="text"
                          value={profileForm.city}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-gray-900">{user.city || 'Not set'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      {editing ? (
                        <input
                          type="text"
                          value={profileForm.state}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-gray-900">{user.state || 'Not set'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PIN Code</label>
                      {editing ? (
                        <input
                          type="text"
                          value={profileForm.pincode}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, pincode: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-gray-900">{user.pincode || 'Not set'}</p>
                      )}
                    </div>
                  </div>

                  {editing && (
                    <div className="flex space-x-3 pt-4">
                      <Button onClick={handleProfileUpdate} disabled={loading}>
                        {loading ? 'Updating...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'verification' && (
              <div className="space-y-6">
                {/* Contact Verification */}
                <Card>
                  <CardHeader>
                    <CardTitle>Contact Verification</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Phone Verification */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{user.phone}</p>
                          <p className="text-sm text-gray-600">Phone Number</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {user.phone_verified ? (
                          <div className="flex items-center space-x-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">Verified</span>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleSendOTP('phone')}
                            disabled={loading}
                          >
                            {loading ? 'Sending...' : 'Verify'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Email Verification */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{user.email}</p>
                          <p className="text-sm text-gray-600">Email Address</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {user.email_verified ? (
                          <div className="flex items-center space-x-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">Verified</span>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleSendOTP('email')}
                            disabled={loading}
                          >
                            {loading ? 'Sending...' : 'Verify'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* KYC Verification */}
                <Card>
                  <CardHeader>
                    <CardTitle>KYC Verification</CardTitle>
                    <p className="text-gray-600">Required for withdrawals and higher limits</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {user.kyc_verified ? (
                      <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">KYC Verified</p>
                          <p className="text-sm text-green-600">Your KYC details have been verified</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!user.phone_verified || !user.email_verified ? (
                          <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <AlertCircle className="h-6 w-6 text-yellow-600" />
                            <div>
                              <p className="font-medium text-yellow-800">Verification Required</p>
                              <p className="text-sm text-yellow-600">
                                Please verify your phone and email before completing KYC
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Aadhar Number
                              </label>
                              <input
                                type="text"
                                value={kycForm.aadhar_number}
                                onChange={(e) => setKycForm(prev => ({ ...prev, aadhar_number: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Enter Aadhar number"
                                maxLength={12}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                PAN Number
                              </label>
                              <input
                                type="text"
                                value={kycForm.pan_number}
                                onChange={(e) => setKycForm(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Enter PAN number"
                                maxLength={10}
                              />
                            </div>

                            <div className="md:col-span-2">
                              <Button
                                onClick={handleKYCUpdate}
                                disabled={loading || !kycForm.aadhar_number || !kycForm.pan_number}
                                className="w-full"
                              >
                                {loading ? 'Submitting...' : 'Submit KYC Details'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'referral' && (
              <Card>
                <CardHeader>
                  <CardTitle>Referral Program</CardTitle>
                  <p className="text-gray-600">Share your referral code and invite friends to join!</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Referral Code Display */}
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <div className="mb-4">
                      <Users className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Your Referral Code</h3>
                      <p className="text-gray-600 text-sm">Share this code with your friends</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-2 border-dashed border-blue-300 mb-4">
                      <div className="text-3xl font-bold text-blue-600 mb-2">{user.referral_code || 'LOADING...'}</div>
                    </div>
                    
                    <div className="flex space-x-3 justify-center">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(user.referral_code || '');
                          setSuccess('Referral code copied to clipboard!');
                        }}
                        className="flex items-center space-x-2"
                      >
                        <Copy className="h-4 w-4" />
                        <span>Copy Code</span>
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const text = `Hey! Join this amazing contest platform using my referral code: ${user.referral_code}`;
                          if (navigator.share) {
                            navigator.share({ text });
                          } else {
                            navigator.clipboard.writeText(text);
                            setSuccess('Referral message copied to clipboard!');
                          }
                        }}
                        className="flex items-center space-x-2"
                      >
                        <Share2 className="h-4 w-4" />
                        <span>Share</span>
                      </Button>
                    </div>
                  </div>

                  {/* How it Works */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">How Referral Works</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-white font-bold text-sm">1</span>
                        </div>
                        <h5 className="font-medium text-gray-900 mb-1">Share Code</h5>
                        <p className="text-sm text-gray-600">Share your referral code with friends</p>
                      </div>
                      
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="bg-green-600 w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <h5 className="font-medium text-gray-900 mb-1">Friends Join</h5>
                        <p className="text-sm text-gray-600">They sign up using your code</p>
                      </div>
                      
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="bg-purple-600 w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-white font-bold text-sm">3</span>
                        </div>
                        <h5 className="font-medium text-gray-900 mb-1">Both Benefit</h5>
                        <p className="text-sm text-gray-600">You and your friend get bonuses</p>
                      </div>
                    </div>
                  </div>

                  {/* Referral Stats */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Your Referral Stats</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-900">0</div>
                        <div className="text-sm text-gray-600">Total Referrals</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-900">0</div>
                        <div className="text-sm text-gray-600">Active Referrals</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'security' && (
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Password</p>
                        <p className="text-sm text-gray-600">Last changed 30 days ago</p>
                      </div>
                      <Button variant="outline">Change Password</Button>
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                        <p className="text-sm text-gray-600">Add an extra layer of security</p>
                      </div>
                      <Button variant="outline">Enable 2FA</Button>
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Login Sessions</p>
                        <p className="text-sm text-gray-600">Manage your active sessions</p>
                      </div>
                      <Button variant="outline">View Sessions</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;